/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import Backbone from 'backbone';
import gql from 'graphql-tag';
import React from 'react';
import ReactDOM from 'react-dom';

import EntryActions from 'actions/EntryActions';
import {assert, assertAndContinue} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AICheckerResultsContainer from 'notebook/ai-tools/AICheckerResultsContainer';
import {canEditEntry} from 'notebook/entries/canEditEntry';
import ConfigureReviewCriteriaSection from 'notebook/reviews/ConfigureReviewCriteriaSection';
import Review from 'notebook/reviews/Review';
import ReviewButtonGroup from 'notebook/reviews/ReviewButtonGroup';
import * as ReviewConstants from 'notebook/reviews/ReviewConstants';
import ReviewStatus from 'notebook/reviews/ReviewStatus';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import EntryStore from 'stores/EntryStore';
import {ButtonSize} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import APIUtil from 'util/APIUtil';
import * as TaskRunner from 'util/TaskRunner';
import {OngoingTaskResponse} from 'util/TaskRunner';
import * as StageEntryActions from 'workflows/stage-entries/StageEntryActions';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';

import {
  ShowReviewViewButtonsQuery,
  ShowReviewViewButtonsQueryVariables,
} from './__generated/ShowReviewViewButtonsQuery';
import {ReviewSummary} from './ReviewConstants';
import {isReviewUpdatedResponse, ReviewUpdatedResponse} from './ReviewDAO';
import ReviewTabState from './ReviewTabState';

export const SHOW_REVIEW_VIEW_BUTTONS_QUERY = gql`
  query ShowReviewViewButtonsQuery($entryId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    entry(id: $entryId) {
      ... on EntryNode {
        id
        project {
          ... on ProjectNode {
            id
            reviewLifecycles {
              id
            }
          }
        }
        review {
          ... on ReviewNode {
            id
            reviewLifecycleVersion {
              id
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

class ReviewTabConnector extends DataConnector<Props, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [EntryStore, StageEntryStore];

  enhanceProps(): EnhancedProps {
    if (this.entryStore().inStore(this.props.fileId)) {
      const editorState = this.entryStore().getEditorState(this.props.fileId);
      // openVersionId can be `false` when no version is open yet; normalize to null.
      const openVersionId = typeof editorState.openVersionId === 'number' ? editorState.openVersionId : null;
      return {
        review: editorState.entry?.review,
        canEditEntry: canEditEntry(editorState),
        openVersionId,
      };
    }
    if (this.stageEntryStore().inStore(this.props.fileId)) {
      // Stage entries have no Entry.allowEdits() equivalent, so fix actions are disabled for them.
      return {
        review: this.stageEntryStore().getEditorState(this.props.fileId).stageEntryDocument.review,
        canEditEntry: false,
        openVersionId: null,
      };
    }
    return {review: undefined, canEditEntry: false, openVersionId: null};
  }
}

interface Props {
  fileId: string;
  permissions: any;
}

interface EnhancedProps {
  review?: ReviewSummary;
  // Whether the open entry can currently be edited (writable, latest, not dirty, not locked for review).
  // Always false for stage entries and when no entry is loaded.
  canEditEntry: boolean;
  // The version id currently open in the editor, used to detect stale Notebook Check results.
  openVersionId: number | null;
}

interface ReviewTabForEntryProps {
  entryId: string;
  review?: ReviewSummary;
  onReviewUpdate: (
    fileId: string,
    review: ReviewConstants.Review,
    permissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;
  permissions: any;
  btnGroupClassName: string;
  canEditEntry: boolean;
  openVersionId: number | null;
}

export function ReviewTabForEntry({
  entryId,
  review,
  onReviewUpdate,
  permissions,
  btnGroupClassName,
  canEditEntry,
  openVersionId,
}: ReviewTabForEntryProps): JSX.Element {
  return (
    <BenchlingQuery<ShowReviewViewButtonsQuery, ShowReviewViewButtonsQueryVariables>
      query={SHOW_REVIEW_VIEW_BUTTONS_QUERY}
      variables={{entryId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({entry}) => (
        <div className="_test-review-tab">
          <div className="buffer-options">
            {!isInaccessible(entry) ? (
              <ReviewButtonGroup
                buttonClassName="u-mini-spacing-left"
                className={btnGroupClassName}
                fileId={entryId}
                review={review}
                onUpdate={onReviewUpdate}
                size={ButtonSize.MINI}
                entryReviewLifecycleInfo={{
                  projectUsesReviewLifecycle: entry.project.reviewLifecycles.length > 0,
                  wasLastSentForLegacyReview:
                    entry.review != null && entry.review.reviewLifecycleVersion == null,
                }}
              />
            ) : null}
            <h5 className="u-small-spacing-left u-small-spacing-right u-display-inline-block">Status:</h5>
            <ReviewStatus review={review} />
          </div>
          <div className="react-tab-container u-small-padding">
            {BenchlingConfig.ELN_ENABLE_AI_CHECKER && (
              <AICheckerResultsContainer
                entryId={entryId}
                canEditEntry={canEditEntry}
                openVersionId={openVersionId}
              />
            )}
            <Review
              id={entryId}
              review={review}
              initialItemsShowing={5}
              onUpdate={onReviewUpdate}
              permissions={permissions}
              hasReviewLifecycle={!isInaccessible(entry) && entry.project.reviewLifecycles.length > 0}
            />
          </div>
        </div>
      )}
    </BenchlingQuery>
  );
}

export class ReviewTab extends React.Component<Props & EnhancedProps> {
  onReviewUpdate = async (
    _fileId: string,
    review: ReviewConstants.Review,
    permissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ): Promise<void> => {
    if (
      BenchlingConfig.ENABLE_FROZEN_CHIPS &&
      APIUtil.apiIdToType(this.props.fileId) === ModelTypeConstants.ENTRY
    ) {
      await ReviewUtil.refreshFrozenChipVersion(this.props.fileId, review.status);
    } else {
      this.dispatchPostReviewUpdateAction(review, permissions);
    }

    if (snapshotTask) {
      TaskRunner.resumeTask<ReviewUpdatedResponse>(snapshotTask.status_url).then((response) => {
        if (isReviewUpdatedResponse(response)) {
          const {review: updatedReview, filePermissions} = response;
          this.dispatchPostReviewUpdateAction(updatedReview, filePermissions);
        }
      });
    }
  };

  dispatchPostReviewUpdateAction = (review: ReviewConstants.Review, permissions: Permissions): void => {
    const fileType = APIUtil.apiIdToType(this.props.fileId);
    if (fileType === ModelTypeConstants.ENTRY) {
      EntryActions.postServerUpdate(this.props.fileId, {review, permissions});
    } else {
      assert(fileType === ModelTypeConstants.STAGE_ENTRY, 'unexpected fileType');
      StageEntryActions.updateReview(this.props.fileId, review, permissions);
    }
  };

  override render(): JSX.Element {
    const fileType = APIUtil.apiIdToType(this.props.fileId);
    if (ReviewUtil.canUserReviewTemplate(fileType)) {
      assertAndContinue(
        BenchlingConfig.ENABLE_ELN_REVIEW_CRITERIA,
        'Review tab should not be shown for templates if ENABLE_ELN_REVIEW_CRITERIA is not set'
      );
      if (!BenchlingConfig.ENABLE_ELN_REVIEW_CRITERIA) {
        return <div />;
      }
      return (
        <div className="react-tab-container u-small-padding">
          <ConfigureReviewCriteriaSection
            linkedItemId={this.props.fileId}
            writable={this.props.permissions.writable}
          />
        </div>
      );
    }

    const btnGroupClassName =
      'pull-right u-tiny-spacing-bottom u-tiny-spacing-top u-small-spacing-right _test-review-buttons';

    if (fileType === ModelTypeConstants.ENTRY) {
      return (
        <ReviewTabForEntry
          entryId={this.props.fileId}
          onReviewUpdate={this.onReviewUpdate}
          review={this.props.review}
          permissions={this.props.permissions}
          btnGroupClassName={btnGroupClassName}
          canEditEntry={this.props.canEditEntry}
          openVersionId={this.props.openVersionId}
        />
      );
    }

    return (
      <div className="_test-review-tab">
        <div className="buffer-options">
          <ReviewButtonGroup
            buttonClassName="u-mini-spacing-left"
            className={btnGroupClassName}
            fileId={this.props.fileId}
            review={this.props.review}
            onUpdate={this.onReviewUpdate}
            size={ButtonSize.MINI}
          />
          <h5 className="u-small-spacing-left u-small-spacing-right u-display-inline-block">Status:</h5>
          <ReviewStatus review={this.props.review} />
        </div>
        <div className="react-tab-container u-small-padding">
          <Review
            id={this.props.fileId}
            review={this.props.review}
            initialItemsShowing={5}
            onUpdate={this.onReviewUpdate}
            permissions={this.props.permissions}
          />
        </div>
      </div>
    );
  }
}

export const ConnectedReviewTab = ReviewTabConnector.connect(ReviewTab);

/* eslint-disable @typescript-eslint/unbound-method */
export class ReviewTabView extends Backbone.View<ReviewTabState> {
  declare history: any;

  static initClass(): void {
    this.prototype.className = 'buffer-tab';
  }

  override initialize(): void {
    this.history = [];
    this.listenTo(this.model.get('model'), 'change', this.rerender);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
  override render() {
    this.rerender();
    return this;
  }

  rerender(): void {
    const fileId = this.model.get('model').id;
    const proxiedObject = this.model.get('model').proxied();
    renderToDOM(<ConnectedReviewTab fileId={fileId} permissions={proxiedObject.permissions} />, this.el);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
  override remove() {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }
}

/* eslint-enable @typescript-eslint/unbound-method */
ReviewTabView.initClass();
